import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import NotificationsIcon from '@mui/icons-material/Notifications';
import { Badge, IconButton } from '@mui/material';

import Notifications from '../Notifications';
import * as S from './styles';
import NotificationTooltip from '../Notifications/NotificationTooltip';
import Text from 'components/Text';
import { useMenuSelection } from 'global/MenuSelectionContext';
import { useThemeContext } from 'global/ThemeContext/ThemeContext';
import { useDynamicLogo } from 'hooks/useDynamicLogo';
import { useMenuTitle } from 'hooks/useMenuTitleContext';
import { useNotificationTooltip } from 'hooks/useNotificationTooltip';
import { useSideBar } from 'hooks/useSideBar';
import { useIsMobile, useIsTablet } from 'utils/mediaQueries';

interface HeaderProps {
  activeSubmenu?: string;
}

const Header: React.FC<HeaderProps> = ({ activeSubmenu }) => {
  const theme = useThemeContext();
  const location = useLocation();
  const { logo, isLoading: logoLoading } = useDynamicLogo();
  const { open, titleMenu } = useSideBar();
  const { menuTitle, updateMenuTitle } = useMenuTitle();
  const { secondLevelMenu, firstLevelMenu } = useMenuSelection();
  const [openNotifications, setOpenNotifications] = useState(false);
  const [newNotifications, setNewNotifications] = useState(0);
  const navigate = useNavigate();

  // Referência para o ícone de notificações
  const notificationIconRef = useRef<HTMLButtonElement>(null);

  // Hook para gerenciar tooltip de notificações
  const { showTooltip, tooltipNotification, handleTooltipClose } = useNotificationTooltip();

  useEffect(() => {
    console.log('[Header] New notifications:', newNotifications);
  }, [newNotifications]);

  const handleClose = () => {
    setOpenNotifications(false);
  };

  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  useEffect(() => {
    let newTitle: string | boolean = menuTitle;

    // If we have a second level menu selected, use its title
    if (secondLevelMenu) {
      newTitle = secondLevelMenu.label;
    }
    // If we have a first level menu selected but no second level, use the first level title
    else if (firstLevelMenu) {
      newTitle = firstLevelMenu.label;
    }
    // Fallback to path-based titles
    else {
      switch (true) {
        case location.pathname.includes('/electoral-profile/wizard'):
          newTitle = 'Wizard Eleitoral';
          break;
        case location.pathname.includes('/acquisitions/signature'):
          newTitle = 'Aquisições';
          break;
        case location.pathname.includes('/settings/profile'):
          newTitle = 'Configurações';
          break;
        case location.pathname.includes('/application-management/team'):
          newTitle = 'Portfólio de Advisors';
          break;
        default:
          newTitle = '';
          break;
      }
    }

    if (typeof newTitle === 'string') {
      updateMenuTitle(newTitle);
    }
  }, [location.pathname, titleMenu, updateMenuTitle, menuTitle, secondLevelMenu, firstLevelMenu]);

  return (
    <>
      <S.HeaderContainer open={open} isMobile={isMobile || isTablet}>
        {isMobile || isTablet ? (
          <S.MobileHeader>
            {!logoLoading && (
              <S.LogoHeaderImage
                src={logo || undefined}
                alt="Logo"
                isMobile={true}
                onClick={() => navigate('/')}
              />
            )}
            <Text size="XXLarge" color="textPrimary" bold>
              {menuTitle || ''}
            </Text>
          </S.MobileHeader>
        ) : (
          <>
            <S.Title>
              <IconButton
                ref={notificationIconRef}
                onClick={() => {
                  setOpenNotifications(true);
                }}
                sx={{
                  marginRight: '1.25rem',
                  marginLeft: '1.25rem',
                }}
              >
                <Badge
                  badgeContent={newNotifications}
                  color="error"
                  max={9}
                  sx={{
                    fontSize: '1rem',
                    color: theme.text.primary,
                  }}
                >
                  <NotificationsIcon
                    style={{
                      fontSize: '1.75rem',
                      color: theme.text.primary,
                    }}
                  />
                </Badge>
              </IconButton>

              <Text size="XXLarge" color="textPrimary" bold>
                {menuTitle || ''}
              </Text>
            </S.Title>
            {!logoLoading && (
              <S.LogoHeaderImage src={logo || undefined} alt="Logo" isMobile={false} />
            )}
          </>
        )}
      </S.HeaderContainer>

      {/* Tooltip de nova notificação para desktop */}
      {!isMobile && !isTablet && tooltipNotification && (
        <NotificationTooltip
          notification={tooltipNotification}
          isVisible={showTooltip}
          onClose={handleTooltipClose}
          position="header"
          anchorEl={notificationIconRef.current}
        />
      )}

      <Notifications
        open={openNotifications}
        onClose={handleClose}
        onNewNotification={setNewNotifications}
      />
    </>
  );
};

export default Header;
